import React, { useEffect, useState } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  ActivityIndicator,
  RefreshControl} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { SafeAreaView } from "react-native-safe-area-context";
import commonStyles from "../../../styles/commonStyles";
import PersonalDetailsStyle from "../PersonalDetails/PersonalDetailsStyle";
import OverflowTooltipText from "../../../components/OverflowTooltipText";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { IUpdatedUserData, IUserTypes } from "../../../types/IUserTypes";
import { ApiService } from "../../../service/ApiService";
import Toast from "react-native-toast-message";
import { getUserIdToUse } from "../../../utils/getUserIdToUse";
import { black } from "react-native-paper/lib/typescript/styles/themes/v2/colors";
import { filterEmail, isValidEmail } from "../../../utils/emailUtilsForSpouse";
import { fonts } from "../../../styles/theme";
import AppText from "../../../components/AppText";
import AppTextInput from "../../../components/AppTextInput";

type AddressProps = {
  navigation: {
    navigate: (screen: string) => void;
    goBack: () => void;
  };
};

type FormData = {
  email: string;
};

const SpouseLinkingPage: React.FC<AddressProps> = ({ navigation }) => {
  const [formData, setFormData] = useState<FormData>({ email: "" });
  const [userData, setUserData] = useState<IUserTypes | null>();
  const [isFocused, setIsFocused] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailValid, setEmailValid] = useState(false); // Track email validity
  const [refreshing, setRefreshing] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const handleFocus = (field: string) =>
    setIsFocused({ ...isFocused, [field]: true });
  const handleBlur = (field: string) =>
    setIsFocused({ ...isFocused, [field]: false });
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (field === "email") {
      setEmailValid(isValidEmail(value)); // Validate email on change
    }
  };

  const sendSpouseRequestByPartner = async (data: any): Promise<any> => {
    const url = "portal/partner/linkSpouseByUser";
    return ApiService.post(url, data);
  };

  const sendSpouseRequestByAdmin = async (data: any): Promise<any> => {
    const url = "portal/partner/linkSpouseByAdmin";
    return ApiService.post(url, data);
  };

  const UserData = async () => {
    try {
      let apiResponse: string | null;
      apiResponse = await AsyncStorage.getItem("apiResponse");
      const userData: IUserTypes = JSON.parse(apiResponse || "{}");
      setUserData(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    UserData();
  }, []);

  const addingSpouse = async (): Promise<void> => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) {
      newErrors["email"] = "Email is required.";
    } else if (!emailValid) {
      newErrors["email"] = "Please enter a valid email address";
    }

    setErrors(newErrors);
    setShowEmailError(true);

    if (Object.keys(newErrors).length > 0) return;

    const requestPayload = {
      partnerEmail: userData?.email || "",
      spouseEmail: formData.email};

    setLoading(true);

    try {
      if (
        Array.isArray(userData?.userRoles) &&
        userData.userRoles[0]?.role === "SA"
      ) {
        const response = await sendSpouseRequestByAdmin(requestPayload);
        Toast.show({
          type: "success",
          position: "top",
          text1: "SpouseRequest is added by admin",
          visibilityTime: 4000});
      } else {
        const response = await sendSpouseRequestByPartner(requestPayload);
        Toast.show({
          type: "success",
          position: "top",
          text1: "Email sent to spouse by partner",
          visibilityTime: 4000});
      }
      fetchUserData();
      setFormData({ email: "" });
      setShowEmailError(false);
    } catch (e: any) {
      console.error("Something went wrong pls try again");
      Toast.show({
        type: "error",
        position: "top",
        text1: e?.response?.data?.responseMessage || "Failed to send email.",
        visibilityTime: 4000});
    } finally {
      setLoading(false);
    }
  };
  const deLinkingSpouseByPartner = async (data: any): Promise<any> => {
    const url = "portal/partner/deLinkSpouse";
    return ApiService.post(url, data);
  };

  const fetchUserData = async () => {
    try {
      const userIdToUse = await getUserIdToUse();
      const response = await ApiService.get<any>(`portal/partner/${userIdToUse}`);

      await AsyncStorage.setItem("apiResponse", JSON.stringify(response.data));
      setUserData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  const deLinkingSpouse = async (): Promise<void> => {
    const requestPayload = {
      partnerEmail: userData?.email || "",
      spouseEmail: userData?.partnerSpouse?.email};
    setLoading(true);
    try {
      const response = await deLinkingSpouseByPartner(requestPayload);
      console.log("Response:", response);
      fetchUserData();

      Toast.show({
        type: "success",
        position: "top",
        text1: "Spouse delinked successfully.",
        visibilityTime: 4000});
    } catch (e: any) {
      console.error("Spouse request error:", e);
      setLoading(false);
      Toast.show({
        type: "error",
        position: "top",
        text1: "something went wrong",
        visibilityTime: 4000});
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchUserData();
  };

  return (
    <SafeAreaView
      edges={["bottom", "left", "right"]}
      style={[PersonalDetailsStyle.container, commonStyles.screenBottomGap]}
    >
      <View style={[PersonalDetailsStyle.subHeaderContainer]}>
        <TouchableOpacity
          testID="backButton"
          style={PersonalDetailsStyle.subHeaderContainer}
          onPress={navigation.goBack}
        >
          <AntDesign name="left" size={24} color="black" />
          <AppText style={commonStyles.subHeader}>Spouse Details</AppText>
        </TouchableOpacity>
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior="padding">
        <ScrollView
          contentContainerStyle={PersonalDetailsStyle.scrollContainer}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
        >
          {userData?.pendingSpouseRequest?.spouseStatus === "P" ? (
            <View style={{ alignItems: "center" }}>
              <AppText style={[styles.subTitle, { textAlign: "center" }]}>
                Invitation sent to your spouse with{" "}
                <AppText style={{ fontWeight: "bold" }}>
                  {userData.pendingSpouseRequest.receiverEmail}
                </AppText>{" "}
                address - pending acceptance
              </AppText>
            </View>
          ) : !userData?.partnerSpouse?.email ? (
            <View>
              <View>
                <AppText style={styles.label} testID="email">
                  SPOUSE EMAIL ADDRESS <AppText style={{ color: "black" }}>*</AppText>
                </AppText>
                <AppTextInput
                  style={[
                    styles.input,
                    { borderColor: isFocused.email ? "#4e2ea2" : "#ddd" },
                  ]}
                  placeholder="Enter Email"
                  placeholderTextColor="#979797"
                  value={formData.email}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  onFocus={() => handleFocus("email")}
                  onBlur={() => handleBlur("email")}
                  onChangeText={(value) =>
                    handleChange("email", filterEmail(value))
                  }
                />
                {showEmailError && errors.email && (
                  <AppText style={styles.errorText}>{errors.email}</AppText>
                )}
              </View>

              <AppText style={styles.subTitle}>
                Your spouse can view all your investments
              </AppText>
              <TouchableOpacity
                testID="Update"
                style={PersonalDetailsStyle.button}
                disabled={loading}
                onPress={addingSpouse}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <AppText style={PersonalDetailsStyle.buttonText}>
                    Add Spouse
                  </AppText>
                )}
              </TouchableOpacity>
            </View>
          ) : (
            <View>
              <View style={[commonStyles.subDetails, { paddingTop: 0 }]}>
                <AppText style={commonStyles.subTitle}>First Name</AppText>
                <OverflowTooltipText
                  text={userData?.partnerSpouse?.firstName || ""}
                  style={commonStyles.subValue}
                  containerWidth="70%"
                />
              </View>
              <View style={commonStyles.subDetails}>
                <AppText style={commonStyles.subTitle}>Last Name</AppText>
                <OverflowTooltipText
                  text={userData?.partnerSpouse?.lastName || ""}
                  style={commonStyles.subValue}
                  containerWidth="70%"
                />
              </View>

              <View style={commonStyles.subDetails}>
                <AppText style={commonStyles.subTitle}>Email</AppText>
                <OverflowTooltipText
                  text={userData?.partnerSpouse?.email || ""}
                  style={commonStyles.subValue}
                  containerWidth="75%"
                />
              </View>
              <AppText style={styles.subTitle}>
                Your spouse can view all your investments
              </AppText>
              <TouchableOpacity
                testID="Update"
                style={PersonalDetailsStyle.button}
                disabled={loading}
                onPress={deLinkingSpouse}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <AppText style={PersonalDetailsStyle.buttonText}>
                    Delink Spouse
                  </AppText>
                )}
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "#fff"},
  scrollContainer: {
    paddingBottom: 20,
    paddingTop: 10},
  label: {
    marginBottom: 8,
    fontSize: 12,
    color: "#979797",
    //fontWeight: "600",
    fontFamily: fonts.regular},
  input: {
    height: 45,
    borderWidth: 1,
    borderRadius: 15,
    paddingHorizontal: 12,
    fontSize: 14,
    color: "#343434",

    //fontWeight: "600",
    borderColor: "#CBCBCB",
    fontFamily: fonts.regular},
  subTitle: {
    fontSize: 12,
    color: "#343434",
    marginTop: 10,
    fontFamily: fonts.regular},
  errorText: {
    color: "red",
    fontSize: 10,
    marginTop: 5,
    fontFamily: fonts.regular}});

export default SpouseLinkingPage;
